import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TypedAction } from '@ngrx/store/src/models';
import { Observable, catchError, concatMap, debounceTime, map, merge, of, switchMap, withLatestFrom } from 'rxjs';
import { environment } from 'src/app/environments/environment';
import { AppType } from 'src/app/model/app-type.model';
import { AgentSaveCalculationPayload, SaveCalculationPayload } from 'src/app/model/save-calculation.model';
import { ProcessService } from 'src/app/services/process.service';
import AppError from '../../model/app-error';
import { AgentPremiumCalculationBody, AgentQuotationBody, PremiumCalculationBody, QuotationBody } from '../../model/offer';
import { ApiService } from '../../services/api.service';
import { getFirstArrayValue } from '../../utils';
import { AppState } from '../index';
import processActions, { ProcessActions } from './process.actions';
import * as fromProcess from './process.selectors';

const discountStartDebounceTime = 200;

@Injectable()
export class ProcessEffects {
  saveCalculationStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.saveCalculationStart),
      switchMap(({ payload }) => {
        switch (environment.app) {
          case AppType.Direct:
            return this.apiService.postSaveCalculation(payload as SaveCalculationPayload).pipe(
              map((success) => processActions.saveCalculationSuccess({ success })),
              catchError((error: AppError) => of(processActions.saveCalculationError({ error }))),
            );
          default:
            return this.apiService.postAgentSaveCalculation(payload as AgentSaveCalculationPayload).pipe(
              map((success) => processActions.saveCalculationSuccess({ success })),
              catchError((error: AppError) => {
                if (error.type === 'auth' || this.router.url === '/error/auth') {
                  void this.router.navigate(['/error/auth']);
                }

                return of(processActions.saveCalculationError({ error }));
              }),
            );
        }
      }),
    ),
  );

  saveOfferCalculationStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.saveOfferCalculationStart),
      switchMap(({ payload }) =>
        this.apiService.postAgentSaveCalculation(payload).pipe(
          switchMap((success) =>
            of(processActions.saveOfferCalculationSuccess({ success }), processActions.saveCalculationSuccess({ success })),
          ),
          catchError((error: AppError) => {
            if (error.type === 'auth' || this.router.url === '/error/auth') {
              void this.router.navigate(['/error/auth']);
            }

            return of(processActions.saveOfferCalculationError({ error }), processActions.saveCalculationError({ error }));
          }),
        ),
      ),
    ),
  );

  confirmStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.confirmStart),
      switchMap(({ payload }) =>
        this.apiService.postConfirm(payload).pipe(
          map((success) => processActions.confirmSuccess({ success })),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          catchError((error) => of(processActions.confirmError({ error }))),
        ),
      ),
    ),
  );

  conclusionStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.conclusionStart),
      switchMap(({ payload }) =>
        this.apiService.postAgentPurchase(payload).pipe(
          map((success) => processActions.conclusionSuccess({ success })),
          catchError((error: AppError) => of(processActions.conclusionError({ error }))),
        ),
      ),
    ),
  );

  sendStaticAttachmentsStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.sendStaticAttachmentsStart),
      switchMap(({ hashCode }) =>
        this.apiService.postAgentSendStaticAttachments(hashCode).pipe(
          map((success) => processActions.sendStaticAttachmentsSuccess({ success })),
          catchError((error: AppError) => of(processActions.sendStaticAttachmentsError({ error }))),
        ),
      ),
    ),
  );

  calculationToClientConfirmStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.calculationToClientConfirmStart),
      switchMap(({ payload }) =>
        this.apiService.postCalculationToClientConfirm(payload).pipe(
          map((success) => processActions.calculationToClientConfirmSuccess({ success })),
          catchError((error: AppError) => of(processActions.calculationToClientConfirmError({ error }))),
        ),
      ),
    ),
  );

  fetchQuotationStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.fetchQuotationStart),
      switchMap(({ body, slim }) => {
        switch (environment.app) {
          case AppType.Direct:
            return this.apiService.postQuotation(body as QuotationBody, slim).pipe(
              map((data) => processActions.fetchQuotationSuccess({ data })),
              catchError((error: AppError) => of(processActions.fetchQuotationError({ error }))),
            );
          default:
            return this.apiService.postAgentQuotation(body as AgentQuotationBody, slim).pipe(
              map((data) => processActions.fetchQuotationSuccess({ data })),
              catchError((error: AppError) => {
                if (error.type === 'auth' || this.router.url === '/error/auth') {
                  void this.router.navigate(['/error/auth']);
                }

                return of(processActions.fetchQuotationError({ error }));
              }),
            );
        }
      }),
    ),
  );

  fetchQuotationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.fetchQuotationSuccess),
      withLatestFrom(this.store.select(fromProcess.selectProcessKind), this.store.select(fromProcess.selectProcessPackageName)),
      switchMap(([{ data }, kind, selectedPackage]) => {
        const getSelectedPackage = () => {
          if (!kind) {
            return '';
          }

          const packages = data.result.path[kind].packages;

          if (packages.map(({ codeName }) => codeName).includes(selectedPackage)) {
            return selectedPackage;
          }

          return packages?.find((p) => p.highlighted)?.codeName || getFirstArrayValue(packages)?.codeName || '';
        };

        return of(processActions.selectPackage({ packageName: getSelectedPackage() }));
      }),
    ),
  );

  selectedPackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.selectPackage),
      withLatestFrom(
        this.store.select(fromProcess.selectProcessKind),
        this.store.select(fromProcess.selectOfferQuotationData),
        this.store.select(fromProcess.selectPackageAddons),
      ),
      switchMap(([{ packageName }, kind, quotationData, packageAddons]) => {
        const getSelectOfferValue = () => {
          if (!kind || !quotationData) {
            return undefined;
          }
          const currentOfferValue = quotationData?.result.path[kind].packages.find(
            (packageOption) => packageOption.codeName === packageName,
          )?.totalValue.amount;

          quotationData?.result.path[kind].packages.find((packageOption) => packageOption.codeName === packageName);

          return currentOfferValue;
        };
        const getSelectOfferValueBeforeDiscounts = () => {
          if (!kind || !quotationData) {
            return undefined;
          }
          const currentOfferValueBeforeDiscounts = quotationData?.result.path[kind].packages.find(
            (packageOption) => packageOption.codeName === packageName,
          )?.totalValueBeforeDiscounts?.amount;

          quotationData?.result.path[kind].packages.find((packageOption) => packageOption.codeName === packageName);

          return currentOfferValueBeforeDiscounts ?? null;
        };

        let selectPackageAddonsActions: Observable<TypedAction<ProcessActions.SelectPackageAddons>>[] | undefined;
        if (kind && quotationData) {
          const currentOffer = quotationData?.result.path[kind].packages.find((packageOption) => packageOption.codeName === packageName);

          selectPackageAddonsActions = currentOffer?.addOns
            .filter((addon) => !Object.keys(packageAddons.state).includes(addon.codeName))
            .map((addon) => of(processActions.selectPackageAddons({ addon: addon.codeName, value: addon.defaultSelected })));
        }

        if (selectPackageAddonsActions) {
          return merge(
            of(processActions.setOfferValue({ offerValue: getSelectOfferValue() ?? null })),
            of(processActions.setOfferValueBeforeDiscounts({ offerValueBeforeDiscounts: getSelectOfferValueBeforeDiscounts() ?? null })),
            ...selectPackageAddonsActions,
          );
        }

        return merge(
          of(processActions.setOfferValue({ offerValue: getSelectOfferValue() ?? null })),
          of(processActions.setOfferValueBeforeDiscounts({ offerValueBeforeDiscounts: getSelectOfferValueBeforeDiscounts() ?? null })),
        );
      }),
    ),
  );

  fetchDiscountCodeStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.fetchDiscountCodeStart),
      withLatestFrom(this.store.select(fromProcess.selectProcessKind)),
      debounceTime(discountStartDebounceTime),
      switchMap(([{ discountCode }, kind]) => {
        if (kind) {
          switch (environment.app) {
            case AppType.Direct:
              return this.apiService.getDiscountCode(kind, discountCode).pipe(
                map((data) => processActions.fetchDiscountCodeSuccess({ data })),
                catchError((error: AppError) => of(processActions.fetchDiscountCodeError({ error }))),
              );
            default:
              return this.apiService.getAgentDiscountCode(kind, discountCode).pipe(
                map((data) => processActions.fetchDiscountCodeSuccess({ data })),
                catchError((error: AppError) => {
                  if (error.type === 'auth' || this.router.url === '/error/auth') {
                    void this.router.navigate(['/error/auth']);
                  }

                  return of(processActions.fetchDiscountCodeError({ error }));
                }),
              );
          }
        } else {
          return of(processActions.fetchDiscountCodeError({ error: null }));
        }
      }),
    ),
  );

  fetchPremiumCalculationStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.fetchPremiumCalculationStart),
      concatMap(({ body }) => {
        switch (environment.app) {
          case AppType.Direct:
            return this.apiService.postPremiumCalculation(body as PremiumCalculationBody).pipe(
              map((data) => processActions.fetchPremiumCalculationSuccess({ data, packageName: body.selectedOffert.packageCodeName })),
              catchError((error: AppError) =>
                of(processActions.fetchPremiumCalculationError({ packageName: body.selectedOffert.packageCodeName, error })),
              ),
            );
          default:
            return this.apiService.postAgentPremiumCalculation(body as AgentPremiumCalculationBody).pipe(
              map((data) => processActions.fetchPremiumCalculationSuccess({ data, packageName: body.selectedOffert.packageCodeName })),
              catchError((error: AppError) => {
                if (error.type === 'auth' || this.router.url === '/error/auth') {
                  void this.router.navigate(['/error/auth']);
                }

                return of(processActions.fetchPremiumCalculationError({ packageName: body.selectedOffert.packageCodeName, error }));
              }),
            );
        }
      }),
    ),
  );

  fetchPremiumCalculationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.fetchPremiumCalculationSuccess),
      switchMap(({ data }) => of(processActions.setOfferValue({ offerValue: data.result.premium }))),
    ),
  );

  usePolicyDataStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.usePolicyDataStart),
      switchMap(({ policyNumber, leadId }) =>
        this.apiService.getPolicyData(policyNumber, leadId).pipe(
          map((success) => {
            this.processService.clearPolicyParamsAndErrors();

            return processActions.usePolicyDataSuccess({ success });
          }),
          catchError((error: AppError) => {
            this.processService.setPolicyDataError(error);
            void this.router.navigate(['page', 'step1']);

            return of(processActions.usePolicyDataError({ error }));
          }),
        ),
      ),
    ),
  );

  knfLimitStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(processActions.knfLimitStart),
      switchMap(({ payload }) =>
        this.apiService.postKnfLimitCheck(payload).pipe(
          map((success) => processActions.knfLimitSuccess({ success })),
          catchError((error: AppError) => {
            if (error.type === 'auth' || this.router.url === '/error/auth') {
              void this.router.navigate(['/error/auth']);
            }

            return of(processActions.saveCalculationError({ error }));
          }),
        ),
      ),
    ),
  );

  constructor(
    private store: Store<AppState>,
    private apiService: ApiService,
    private actions$: Actions,
    private router: Router,
    private processService: ProcessService,
  ) {}
}
